/**
 * Maater UI gold-standard gallery (?test=maater-ui)
 *
 * Agent SoT for shell decisions. Skinned to production light/dark (ThemeProvider).
 * Feature prototypes (Flows Studio, Drive, Inbox) may still demo Paper/Ink/Stone -
 * they consume these contracts; they are not the icon/menu SoT.
 *
 * Skill: .cursor/skills/maater-design-ui/SKILL.md
 * Continuity: docs/design/maater-list-surface-continuity.md
 * Inventory: docs/design/maater-surface-inventory.md
 */
import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import {
  Activity,
  AlignJustify,
  AppWindowMac,
  Archive,
  BookOpen,
  Bookmark,
  BookmarkX,
  Check,
  CheckCircle2,
  ExternalLink,
  FileDown,
  FileText,
  Folder,
  FolderOpen,
  Inbox,
  Info,
  LayoutGrid,
  Link,
  List,
  Loader2,
  LogOut,
  Maximize2,
  MessageSquare,
  MoreHorizontal,
  MoreVertical,
  NotebookTabs,
  PanelLeft,
  Pencil,
  Pin,
  RefreshCw,
  Settings,
  Sparkles,
  SquareArrowOutUpRight,
  Trash2,
  Undo,
  Workflow,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { maaterProtoAppSkinStyle } from '@/components/prototype/maater-proto-shell'
import {
  ProtoAppHeader,
  ProtoIconRail,
  ProtoModePanel,
  ProtoStackNav,
  ProtoTabRow,
  ProtoTitleRow,
  type ProtoStackNavNode,
} from '@/components/prototype/maater-proto-chrome'
import { DocumentInfoDock, type InfoDockMode } from '@/components/features/document-info-dock'
import { SoulProposalDecisionEvent } from '@/components/features/inbox/SoulProposalDecisionEvent'
import { SoftBookmarkMenuLabelRow } from '@/components/layout/document-soft-bookmark-menu'
import { PreviewPanelToggle } from '@/components/integrations/_shared/preview-panel-toggle'
import { ExtensionListRow } from '@/components/integrations/_shared/extension-list-row'
import { PreviewPaneHeader } from '@/components/features/preview-pane-header'
import { ImageSearchGallery } from '@/components/features/chat-messages/image-search-gallery'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { UpOneLevelRow } from '@/components/ui/up-one-level-row'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import {
  QUIET_MENU_DESTRUCTIVE_ITEM_CLASS,
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
  QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS,
  QUIET_OVERFLOW_MENU_GALLERY_SCROLL_DEMO_CLASS,
  QUIET_OVERFLOW_MENU_LABEL_CLASS,
} from '@/lib/menu-chrome'
import {
  AppWindowPlus,
  OPEN_DESTINATION_SPECS,
  OpenDestinationIcons,
  resolveOpenExternalIcon,
} from '@/lib/open-destination-icons'
import { isModClick } from '@/lib/open-intent'
import { KEYBOARD_SHORTCUTS_CATALOG } from '@/lib/keyboard-shortcuts-catalog'
import { BasecampIcon } from '@/components/icons/basecamp-icon'
import { GoogleDriveIcon } from '@/components/icons/google-drive-icon'
import { useTheme } from '@/providers/theme-provider'
import { PresenceScene } from '@/components/prototype/maater-ui-presence-scene'
import { PanelMotionScene } from '@/components/prototype/maater-ui-panel-motion-scene'
import { PanelCollapseScene } from '@/components/prototype/maater-ui-panel-collapse-scene'
import { TabGroupsScene } from '@/components/prototype/maater-ui-tab-groups-scene'
import { OpenEchoScene } from '@/components/prototype/maater-ui-open-echo-scene'
import { SettingsChromeScene } from '@/components/prototype/maater-ui-settings-chrome-scene'
import { ListOverflowScene } from '@/components/prototype/maater-ui-list-overflow-scene'
import { CreateInFolderScene } from '@/components/prototype/maater-ui-create-in-folder-scene'
import { PersonViewScene } from '@/components/prototype/maater-ui-person-view-scene'
import { ToastChromeScene } from '@/components/prototype/maater-ui-toast-chrome-scene'
import { ComposerOffersScene } from '@/components/prototype/maater-ui-composer-offers-scene'
import { DiffReviewScene } from '@/components/prototype/maater-ui-diff-review-scene'
import { InboxAttentionScene } from '@/components/prototype/maater-ui-gallery-inbox-attention'
import { IndexChromeScene } from '@/components/prototype/maater-ui-index-chrome-scene'
import { SidebarTreeScene } from '@/components/prototype/maater-ui-sidebar-tree-scene'
import { EntityContextMenu } from '@/components/features/entity-context-menu'
import { ModePanelNavItem } from '@/components/layout/sidebar/ModePanelNavItem'
import { wsNavSectionStyle } from '@/lib/ws-nav'
import { SCENES, SceneStatusBanner, findScene, type SceneId } from '@/components/test-maater-ui.scenes'

const IMAGE_SEARCH_GALLERY_DEMO_HITS = [
  {
    title: 'Tufted Puffin on a Pacific Northwest cliff',
    image_url: 'https://picsum.photos/id/1024/400/300',
    thumbnail_url: 'https://picsum.photos/id/1024/400/300',
    source_url: 'https://example.com/puffin',
  },
  {
    title: 'Bald Eagles in the Pacific Northwest',
    image_url: 'https://picsum.photos/id/1015/400/300',
    thumbnail_url: 'https://picsum.photos/id/1015/400/300',
    source_url: 'https://example.com/eagle',
  },
  {
    title: 'Peregrine Falcon perched on coastal rock',
    image_url: 'https://picsum.photos/id/1016/400/300',
    thumbnail_url: 'https://picsum.photos/id/1016/400/300',
    source_url: 'https://example.com/falcon',
  },
]

const CLICK_MODIFIER_SHORTCUTS = KEYBOARD_SHORTCUTS_CATALOG.find(
  (c) => c.category === 'Click modifiers',
)?.items ?? []
const SIDEBAR_NEW_TAB_ENTER = KEYBOARD_SHORTCUTS_CATALOG.flatMap((c) => c.items).find(
  (i) => i.id === 'sidebar-enter-new-tab',
)


/** Gallery-only ranked-out candidates for "Open in new tab" - locked SoT is AppWindowPlus. */
const NEW_TAB_ICON_CANDIDATES: {
  id: 'app-window-plus' | 'square-arrow' | 'notebook-tabs' | 'app-window-mac'
  label: string
  note: string
  icon: LucideIcon
}[] = [
  {
    id: 'app-window-plus',
    label: 'AppWindowPlus',
    note: 'Locked SoT - in-app window + plus (custom glyph)',
    icon: AppWindowPlus as LucideIcon,
  },
  {
    id: 'square-arrow',
    label: 'SquareArrowOutUpRight',
    note: 'Retired - ranked out (escape-arrow; too similar to External)',
    icon: SquareArrowOutUpRight,
  },
  {
    id: 'notebook-tabs',
    label: 'NotebookTabs',
    note: 'Ranked out - closest Lucide "tabs" metaphor',
    icon: NotebookTabs,
  },
  {
    id: 'app-window-mac',
    label: 'AppWindowMac',
    note: 'Ranked out - overlaps Open (AppWindow)',
    icon: AppWindowMac,
  },
]


/** Maater-flavored demo tree - nested depth without expand/collapse. */
const STACK_NAV_DEMO: ProtoStackNavNode[] = [
  { id: 'inbox', label: 'Inbox', icon: Inbox },
  { id: 'browse', label: 'Browse', icon: Folder },
  {
    id: 'flows',
    label: 'Flows',
    icon: Zap,
    children: [
      { id: 'flows-pulse', label: 'Pulse', icon: Activity },
      { id: 'flows-builder', label: 'Builder', icon: Workflow },
      { id: 'flows-gallery', label: 'Gallery', icon: LayoutGrid },
      { id: 'flows-composer', label: 'Composer', icon: Sparkles },
      {
        id: 'flows-sources',
        label: 'Sources',
        icon: BookOpen,
        section: 'Catalog',
      },
      {
        id: 'flows-runs',
        label: 'Run history',
        icon: Activity,
        section: 'Telemetry',
      },
      {
        id: 'flows-webhooks',
        label: 'Webhooks',
        icon: Workflow,
        badge: { label: 'Beta', tone: 'info' },
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    children: [
      { id: 'settings-workspace', label: 'Workspace' },
      { id: 'settings-members', label: 'Members' },
      {
        id: 'settings-extensions',
        label: 'Extensions',
        children: [
          { id: 'ext-basecamp', label: 'Basecamp' },
          { id: 'ext-drive', label: 'Google Drive' },
          { id: 'ext-asana', label: 'Asana', badge: { label: 'Legacy', tone: 'muted' } },
        ],
      },
    ],
  },
]

function sceneFromHash(): SceneId {
  const raw = (window.location.hash || '').replace(/^#/, '')
  if (SCENES.some((s) => s.id === raw)) return raw as SceneId
  return 'open-destinations'
}

function DemoRow({
  selected,
  onSelect,
  onOpen,
  onOpenInNewTab,
  onContextMenu,
  label,
  meta,
}: {
  selected: boolean
  onSelect: () => void
  /** Plain double-click / Enter-style deep open */
  onOpen: () => void
  /** Cmd/Ctrl+click (and mod+double-click) - same as resolveOpenIntent / isModClick */
  onOpenInNewTab: () => void
  onContextMenu?: (e: MouseEvent) => void
  label: string
  meta: string
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        if (isModClick(e)) {
          onSelect()
          onOpenInNewTab()
          return
        }
        onSelect()
      }}
      onDoubleClick={(e) => {
        if (isModClick(e)) {
          onOpenInNewTab()
          return
        }
        onOpen()
      }}
      onContextMenu={onContextMenu}
      className={cn(
        'flex w-full items-center justify-between rounded-md border border-l-2 px-3 py-2 text-left text-sm transition-colors',
        selected
          ? 'border-primary border-l-primary bg-primary/10 font-medium text-foreground'
          : 'border-transparent border-l-transparent bg-[color:var(--paper-raised)] text-foreground hover:bg-muted/50',
      )}
      style={
        selected
          ? undefined
          : { borderColor: 'var(--line)', borderLeftColor: 'transparent' }
      }
    >
      <span>{label}</span>
      <span className="text-xs text-muted-foreground">{meta}</span>
    </button>
  )
}

const PREVIEW_DRILL_L1 = [
  { id: 'spec-1', label: 'docs', meta: 'Folder' },
  { id: 'spec-2', label: 'system', meta: 'Folder' },
  { id: 'themes', label: 'themes', meta: 'Folder' },
] as const

const PREVIEW_DRILL_L2: Record<string, Array<{ id: string; label: string; meta: string }>> = {
  'spec-1': [{ id: 'readme', label: 'README.md', meta: '6 years ago' }],
  'spec-2': [{ id: 'config', label: 'config.php', meta: '6 years ago' }],
  themes: [{ id: 'user', label: 'user', meta: 'Folder' }],
}

/** Collaborative preview: peek follows the selected main row; folder click moves main. */
function PreviewDrillDemo() {
  const [previewOpen, setPreviewOpen] = useState(true)
  const [mainFolder, setMainFolder] = useState('My Drive')
  const [selectedId, setSelectedId] = useState('pub')
  const [lastOpen, setLastOpen] = useState('(none)')

  const mainRows =
    mainFolder === 'My Drive'
      ? [{ id: 'pub', label: 'publisher-2112', meta: '6 years ago' }]
      : [...PREVIEW_DRILL_L1]

  const selected =
    mainRows.find((row) => row.id === selectedId) ??
    (selectedId === 'pub'
      ? { id: 'pub', label: 'publisher-2112', meta: '6 years ago' }
      : PREVIEW_DRILL_L1.find((row) => row.id === selectedId) ?? null)

  const previewRows =
    selected?.id === 'pub' ? [...PREVIEW_DRILL_L1] : selected ? PREVIEW_DRILL_L2[selected.id] ?? [] : []

  const showInMain = (label: string) => {
    setMainFolder(label)
    setSelectedId('')
    setLastOpen(`Main: ${label}`)
  }

  const peekChild = (row: { id: string; label: string }) => {
    if (!selected) return
    setMainFolder(selected.label)
    setSelectedId(row.id)
    setLastOpen(`Main: ${selected.label} · selected ${row.label}`)
  }

  return (
    <div className="space-y-3" data-testid={testIds.maaterUiGallery.previewDrill}>
      <p className="text-xs text-muted-foreground">
        Preview is a peek of the selected main row, not a second browser. Clicking a folder in
        preview enters that parent in main (sidebar follows). Distinct from{' '}
        <code>#mode-panel-stack</code>. Hairline divider only - never <code>w-1</code> plus{' '}
        <code>border-l</code>. Nested todo destinations stay on <code>#list-gestures</code>.
      </p>
      <div className="flex h-[420px] overflow-hidden rounded-lg border border-border bg-background">
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {mainFolder}
            </span>
            <PreviewPanelToggle
              open={previewOpen}
              onToggle={() => setPreviewOpen((open) => !open)}
              testId={testIds.maaterUiGallery.previewToggle}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {mainRows.map((row) => (
              <ExtensionListRow
                key={row.id}
                selected={selectedId === row.id}
                onSelect={() => setSelectedId(row.id)}
                onOpen={() => showInMain(row.label)}
                onContextMenu={() => undefined}
              >
                <Folder className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{row.label}</div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    Main: click selects · double-click enters folder
                  </div>
                </div>
              </ExtensionListRow>
            ))}
            <p className="px-4 py-2 text-xs text-muted-foreground">Last action: {lastOpen}</p>
          </div>
        </div>
        {previewOpen ? (
          <>
            <div className="w-px shrink-0 bg-border" aria-hidden />
            <aside
              className="flex w-[280px] shrink-0 flex-col overflow-hidden bg-card"
              data-testid={testIds.maaterUiGallery.previewDrillPane}
            >
              <PreviewPaneHeader
                icon={<Folder className="h-4 w-4 shrink-0 text-muted-foreground" />}
                label={selected ? 'Folder Preview' : 'Preview'}
                chips={
                  selected ? (
                    <span className="flex-shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wider text-muted-foreground">
                      Folder
                    </span>
                  ) : undefined
                }
                onOpen={selected ? () => showInMain(selected.label) : undefined}
                openTitle="Open folder"
                onClose={() => setPreviewOpen(false)}
              />
              {selected ? (
                <nav
                  className="flex min-w-0 items-center gap-1 border-b border-border px-4 py-2 text-xs text-muted-foreground"
                  aria-label="Preview location"
                >
                  <button
                    type="button"
                    className="truncate text-foreground/80 hover:text-foreground hover:underline underline-offset-2"
                    onClick={() => showInMain(selected.label)}
                  >
                    {selected.label}
                  </button>
                </nav>
              ) : null}
              <div className="min-h-0 flex-1 overflow-y-auto">
                {!selected ? (
                  <p className="p-4 text-xs leading-relaxed text-muted-foreground">
                    Select a file or folder to preview, or choose Preview from the row menu.
                  </p>
                ) : (
                  previewRows.map((row) => (
                  <ExtensionListRow
                    key={row.id}
                    selected={false}
                    testId={
                      selected?.id === 'pub' && row.id.startsWith('spec-')
                        ? testIds.maaterUiGallery.previewDrillRow(row.id)
                        : undefined
                    }
                    onSelect={() => peekChild(row)}
                    onOpen={() => peekChild(row)}
                    onContextMenu={() => undefined}
                    className="[&_[role=button]]:px-4 [&_[role=button]]:py-2.5"
                  >
                    <Folder className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{row.label}</div>
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">{row.meta}</div>
                    </div>
                  </ExtensionListRow>
                ))
                )}
              </div>
            </aside>
          </>
        ) : null}
      </div>
      <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
        <li>
          Folder click in preview: main enters the peeked folder and selects the child. Sidebar
          matches main.
        </li>
        <li>
          Preview crumb (including current) and header Open: that folder becomes the main listing.
        </li>
        <li>
          Opening the peeked folder in main (crumb, Open, or double-click) clears preview to
          nothing selected - do not duplicate the main listing.
        </li>
        <li>
          Divider and fill still hold: hairline <code>w-px bg-border</code>, fill <code>bg-card</code>.
          The header drawn here is the stale part. Copy <code>PeekHeader</code> from{' '}
          <code>#panel-collapse</code>.
        </li>
      </ul>
    </div>
  )
}

/** Interactive teach for rail snap, floors, and auto-hide. */
function PanelCollapseDemo() {
  return <PanelCollapseScene />
}

function MenuChromeBookmarkDemo() {
  const [enabled, setEnabled] = useState(true)
  return (
    <>
      <DropdownMenuSeparator />
      <SoftBookmarkMenuLabelRow enabled={enabled} onEnabledChange={setEnabled} />
      {enabled && (
        <>
          <DropdownMenuItem>
            <Bookmark className="h-4 w-4" />
            Bookmark here
          </DropdownMenuItem>
          <DropdownMenuItem disabled>
            <Bookmark className="h-4 w-4" />
            Jump to bookmark
          </DropdownMenuItem>
          <DropdownMenuItem disabled>
            <BookmarkX className="h-4 w-4" />
            Clear bookmark
          </DropdownMenuItem>
        </>
      )}
    </>
  )
}

export function MaaterUiGalleryTest() {
  const { theme, setTheme } = useTheme()
  const [scene, setScene] = useState<SceneId>(() => sceneFromHash())
  const activeScene = findScene(scene)
  const [sidebarDense, setSidebarDense] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(true)
  const [selectedRow, setSelectedRow] = useState<string | null>('doc-1')
  const [lastOpen, setLastOpen] = useState<string>('(none)')
  const [syncState, setSyncState] = useState<'idle' | 'syncing' | 'synced' | 'conflict'>('idle')
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null)
  const [stackLeaf, setStackLeaf] = useState<string>('flows-pulse')
  const [stackTrail, setStackTrail] = useState('Flows / Pulse')
  const [infoDockMode, setInfoDockMode] = useState<InfoDockMode>('details')
  const [harnessNav, setHarnessNav] = useState<'unread' | 'inbox' | 'flows'>('unread')
  const [newTabIconPick, setNewTabIconPick] = useState<
    (typeof NEW_TAB_ICON_CANDIDATES)[number]['id']
  >('app-window-plus')

  useEffect(() => {
    const onHash = () => setScene(sceneFromHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const selectScene = (id: SceneId) => {
    setScene(id)
    window.history.replaceState(null, '', `?test=maater-ui#${id}`)
  }

  const AgentIcon = OpenDestinationIcons.openInAgentPanel
  const PreviewIcon = OpenDestinationIcons.preview
  const ChatIcon = OpenDestinationIcons.openInChat
  const OpenIcon = OpenDestinationIcons.open

  const menuEntries = useMemo(
    () => [
      {
        label: 'Open in new tab',
        icon: OpenDestinationIcons.openInNewTab,
        onSelect: () => setLastOpen('Open in new tab'),
      },
      {
        label: 'Preview',
        icon: PreviewIcon,
        onSelect: () => {
          setPreviewOpen(true)
          setLastOpen('Preview')
        },
      },
      { separator: true as const },
      {
        label: 'Open in agent panel',
        icon: AgentIcon,
        onSelect: () => setLastOpen('Open in agent panel'),
      },
      {
        label: 'Open in chat',
        icon: ChatIcon,
        onSelect: () => setLastOpen('Open in chat'),
      },
      { separator: true as const },
      { label: 'Archive', icon: Archive, onSelect: () => setLastOpen('Archive') },
    ],
    [PreviewIcon, AgentIcon, ChatIcon],
  )

  return (
    <div
      className="flex h-screen min-h-screen flex-col bg-background text-foreground antialiased"
      style={maaterProtoAppSkinStyle()}
      data-maater-gallery-skin="app"
      data-testid={testIds.maaterUiGallery.root}
    >
      <ProtoAppHeader
        themeControl="light-dark"
        appTheme={theme}
        onAppTheme={(t) => {
          void setTheme(t)
        }}
        badge="maater-ui"
        testId={testIds.maaterUiGallery.appHeader}
        prototypeIndexTestId={testIds.maaterUiGallery.prototypeIndex}
      />
      <div
        className="flex min-h-0 flex-1"
        data-sidebar-density={sidebarDense ? 'dense' : 'detailed'}
      >
        <ProtoIconRail active="documents" testId={testIds.maaterUiGallery.iconRail} />
        <ProtoModePanel
          title="Scenes"
          subtitle="Gallery nav only - not a product mode. Selected-row style matches live Browse/Inbox."
          testId={testIds.maaterUiGallery.modePanel}
          density={sidebarDense}
          onDensityChange={setSidebarDense}
        >
          <nav
            className="ws-nav-section -mx-2 flex flex-col"
            style={wsNavSectionStyle()}
            data-testid={testIds.maaterUiGallery.sceneNav}
          >
            {SCENES.map((s) => (
              <ModePanelNavItem
                key={s.id}
                active={scene === s.id}
                label={
                  <span className={cn(s.status !== 'current' && 'opacity-50')}>{s.label}</span>
                }
                title={s.status === 'superseded' ? `Superseded by #${s.supersededBy}` : undefined}
                onClick={() => selectScene(s.id)}
                data-testid={testIds.maaterUiGallery.sceneButton(s.id)}
              />
            ))}
          </nav>
        </ProtoModePanel>

        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <ProtoTabRow
            tabs={[
              { id: 'gallery', label: 'Gallery' },
              { id: 'hs-doc', label: 'Soul update' },
            ]}
            activeId="gallery"
            onSelect={() => {}}
            testId={testIds.maaterUiGallery.tabRow}
          />
          <ProtoTitleRow
            crumbs={[
              { id: 'gallery', label: 'Maater UI' },
              { id: scene, label: activeScene?.label ?? scene },
            ]}
            title={activeScene?.label ?? scene}
            subtitle={activeScene?.summary}
            testId={testIds.maaterUiGallery.titleRow}
            actions={
              scene === 'list-gestures' || scene === 'entity-menu' ? (
                <PreviewPanelToggle
                  open={previewOpen}
                  onToggle={() => setPreviewOpen((v) => !v)}
                  testId={testIds.maaterUiGallery.previewToggle}
                />
              ) : undefined
            }
          />

          <div className="flex min-h-0 flex-1 overflow-hidden">
            <div
              className="min-h-0 flex-1 overflow-y-auto p-4"
              data-testid={testIds.maaterUiGallery.sceneBody}
              data-proto-status={activeScene?.status}
            >
              <SceneStatusBanner scene={activeScene} />
              {scene === 'open-destinations' && (
                <div className="space-y-4" data-testid={testIds.maaterUiGallery.openDestinations}>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>
                      Import from <code className="text-xs">@/lib/open-destination-icons</code>. Do
                      not invent lucide icons for these labels.
                    </p>
                    <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs leading-relaxed">
                      <p className="font-medium text-foreground">Easy-to-confuse destinations</p>
                      <ul className="mt-1 list-disc space-y-1 pl-4">
                        <li>
                          <strong className="text-foreground">Open in new tab</strong> vs{' '}
                          <strong className="text-foreground">Open in {'{External}'}</strong> - new
                          tab is locked to <code>AppWindowPlus</code> (in-app window + plus). External
                          uses the <em>limb brand icon</em> (Drive, Basecamp); <code>Globe</code> only
                          when unknown. Never <code>ExternalLink</code> or{' '}
                          <code>SquareArrowOutUpRight</code>.
                        </li>
                        <li>
                          <strong className="text-foreground">Open in agent panel</strong> - where to
                          host an <em>existing chat</em> (<code>Bot</code>; right agent pane vs editor
                          tab).
                        </li>
                        <li>
                          <strong className="text-foreground">Open in chat</strong> - start or focus a
                          chat <em>about this item</em> (<code>MessageSquarePlus</code>). Kind glyph on
                          chat rows/tabs stays <code>MessageSquare</code>. Not a layout toggle.
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    {OPEN_DESTINATION_SPECS.map((spec) => {
                      const Icon = spec.icon
                      const iconLabel = spec.icon.displayName || spec.id
                      return (
                        <div
                          key={spec.id}
                          className="flex items-start gap-3 rounded-md border px-3 py-2"
                          style={{
                            borderColor: 'var(--line)',
                            background: 'var(--paper-raised)',
                          }}
                          data-testid={testIds.maaterUiGallery.openDestinationRow(spec.id)}
                        >
                          <Icon className="mt-0.5 h-4 w-4 shrink-0" style={{ color: 'var(--rust)' }} />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
                              {spec.label}
                            </div>
                            <div className="text-xs" style={{ color: 'var(--ink-soft)' }}>
                              {spec.meaning}
                            </div>
                            <div className="mt-1 text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                              Must not reuse for: {spec.mustNotReuseFor.join(', ')}
                            </div>
                          </div>
                          <code className="shrink-0 text-[10px]" style={{ color: 'var(--ink-soft)' }}>
                            {iconLabel}
                          </code>
                        </div>
                      )
                    })}
                  </div>
                  <div
                    className="rounded-md border border-border bg-muted/20 p-3"
                    data-testid={testIds.maaterUiGallery.newTabIconPicker}
                  >
                    <div className="mb-1 text-xs font-semibold text-foreground">
                      Open in new tab - locked + ranked-out candidates
                    </div>
                    <p className="mb-3 text-[11px] text-muted-foreground">
                      Locked SoT is <code>AppWindowPlus</code> in{' '}
                      <code>open-destination-icons.ts</code>. Click ranked-out glyphs below to compare
                      in this gallery only - production menus use the locked icon.
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                      {NEW_TAB_ICON_CANDIDATES.map((c) => {
                        const Icon = c.icon
                        const active = c.id === newTabIconPick
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => setNewTabIconPick(c.id)}
                            data-testid={testIds.maaterUiGallery.newTabIconOption(c.id)}
                            className={cn(
                              'flex flex-col items-start gap-2 rounded-md border px-3 py-3 text-left transition-colors',
                              active
                                ? 'border-primary bg-primary/10'
                                : 'border-border bg-card hover:bg-muted/50',
                            )}
                          >
                            <Icon
                              className={cn(
                                'h-7 w-7',
                                active ? 'text-primary' : 'text-foreground',
                              )}
                              strokeWidth={1.75}
                            />
                            <div>
                              <div className="text-xs font-semibold text-foreground">{c.label}</div>
                              <div className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
                                {c.note}
                              </div>
                            </div>
                            {active ? (
                              <span className="text-[10px] font-medium text-primary">
                                {c.id === 'app-window-plus' ? 'Locked' : 'Preview'}
                              </span>
                            ) : null}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <div
                    className="rounded-md border p-3"
                    style={{ borderColor: 'var(--line)', background: 'var(--cream-card)' }}
                  >
                    <div className="mb-2 text-xs font-semibold" style={{ color: 'var(--ink)' }}>
                      Glyph checklist (must stay distinct)
                    </div>
                    <div className="flex flex-col gap-1 text-sm">
                      <div className="flex items-center gap-2">
                        <OpenDestinationIcons.open className="h-3.5 w-3.5" /> Open (AppWindow)
                      </div>
                      <div className="flex items-center gap-2">
                        <OpenDestinationIcons.openInNewTab className="h-3.5 w-3.5" /> Open in new tab
                        (AppWindowPlus)
                      </div>
                      <div className="flex items-center gap-2">
                        <AgentIcon className="h-3.5 w-3.5" /> Open in agent panel (Bot)
                      </div>
                      <div className="flex items-center gap-2">
                        <ChatIcon className="h-3.5 w-3.5" /> Open in chat (MessageSquarePlus)
                      </div>
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" /> Chat thread kind
                        glyph (MessageSquare - not Open in chat)
                      </div>
                      <div className="flex items-center gap-2">
                        <OpenDestinationIcons.previewPanelToggle className="h-3.5 w-3.5" /> Show /
                        hide preview pane (PanelRight)
                      </div>
                    </div>
                    <div className="mt-3 border-t border-border pt-3">
                      <div className="mb-2 text-xs font-semibold" style={{ color: 'var(--ink)' }}>
                        Open in {'{External}'} - brand first (
                        <code className="text-[10px]">resolveOpenExternalIcon</code>)
                      </div>
                      <div className="flex flex-col gap-1.5 text-sm">
                        <div className="flex items-center gap-2">
                          {(() => {
                            const Icon = resolveOpenExternalIcon(BasecampIcon)
                            return <Icon className="h-3.5 w-3.5" />
                          })()}
                          Open in Basecamp
                        </div>
                        <p className="text-[10px] leading-snug text-muted-foreground">
                          Linked work spec: View todo uses AppWindow (in-app panel). Open in
                          Basecamp uses the mountain. Never ExternalLink for the limb.
                        </p>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const Icon = resolveOpenExternalIcon(GoogleDriveIcon)
                            return <Icon className="h-3.5 w-3.5" />
                          })()}
                          Open in Drive
                        </div>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const Icon = resolveOpenExternalIcon()
                            return <Icon className="h-3.5 w-3.5" />
                          })()}
                          Open in… (unknown host - Globe fallback)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {scene === 'sidebar-tree' && <SidebarTreeScene />}

              {scene === 'list-gestures' && (
                <div className="space-y-3" data-testid={testIds.maaterUiGallery.listGestures}>
                  <p className="text-xs text-muted-foreground">
                    Click to select · ⌘/Ctrl+click open in new tab · Right-click for menu ·
                    Double-click open · PanelRight pins preview. Preview replaces the Info dock
                    on indexes - never a second right-hand column. Up one level is this list
                    only, when the parent row is not on screen. An expand-in-place sidebar
                    tree does not show it.
                  </p>
                  <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
                    <p className="font-medium text-foreground">Registry (do not invent modifiers)</p>
                    <ul className="mt-1 list-disc space-y-1 pl-4">
                      <li>
                        Code: <code>isModClick</code> / <code>resolveOpenIntent</code> in{' '}
                        <code>@/lib/open-intent</code> (ADR-0016) - mod-click →{' '}
                        <code>intent: &apos;new&apos;</code>
                      </li>
                      <li>
                        Catalog:{' '}
                        <code>KEYBOARD_SHORTCUTS_CATALOG</code> → category{' '}
                        <code>Click modifiers</code> / id <code>click-new-tab</code>
                        {CLICK_MODIFIER_SHORTCUTS[0] ? (
                          <>
                            {' '}
                            ({CLICK_MODIFIER_SHORTCUTS[0].mac.join('+')} /{' '}
                            {CLICK_MODIFIER_SHORTCUTS[0].pc.join('+')})
                          </>
                        ) : null}
                      </li>
                      {SIDEBAR_NEW_TAB_ENTER ? (
                        <li>
                          Keyboard: <code>{SIDEBAR_NEW_TAB_ENTER.id}</code> -{' '}
                          {SIDEBAR_NEW_TAB_ENTER.mac.join('+')} (sidebar focus) also opens a new tab
                        </li>
                      ) : null}
                    </ul>
                  </div>
                  <div className="space-y-1">
                    <UpOneLevelRow
                      onClick={() => setLastOpen('Up one level')}
                      testId={testIds.maaterUiGallery.listGesturesUp}
                    />
                    {[
                      { id: 'doc-1', label: 'values.md', meta: 'Hop Studios' },
                      { id: 'doc-2', label: 'identity.md', meta: 'Hop Studios' },
                      { id: 'doc-3', label: 'weekly triage', meta: 'Personal' },
                    ].map((row) => (
                      <DemoRow
                        key={row.id}
                        selected={selectedRow === row.id}
                        label={row.label}
                        meta={row.meta}
                        onSelect={() => setSelectedRow(row.id)}
                        onOpen={() => setLastOpen(`Deep open ${row.label}`)}
                        onOpenInNewTab={() =>
                          setLastOpen(`Open in new tab (⌘/Ctrl+click) · ${row.label}`)
                        }
                        onContextMenu={(e) => {
                          e.preventDefault()
                          setSelectedRow(row.id)
                          setMenuAt({ x: e.clientX, y: e.clientY })
                          setLastOpen(`Context menu · ${row.label}`)
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">Last action: {lastOpen}</p>
                  {menuAt ? (
                    <EntityContextMenu
                      x={menuAt.x}
                      y={menuAt.y}
                      entries={menuEntries}
                      onClose={() => setMenuAt(null)}
                      data-testid={testIds.maaterUiGallery.entityMenuPopup}
                    />
                  ) : null}
                </div>
              )}

              {scene === 'index-chrome' && <IndexChromeScene />}

              {scene === 'preview-drill' && <PreviewDrillDemo />}

              {scene === 'entity-menu' && (
                <div className="space-y-3" data-testid={testIds.maaterUiGallery.entityMenu}>
                  <p className="text-xs" style={{ color: 'var(--ink-soft)' }}>
                    Right-click the row or use ... - same entries (ADR-0020).
                  </p>
                  <div
                    className="flex items-center justify-between rounded-md border px-3 py-2"
                    style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
                    onContextMenu={(e) => {
                      e.preventDefault()
                      setMenuAt({ x: e.clientX, y: e.clientY })
                    }}
                  >
                    <span className="text-sm" style={{ color: 'var(--ink)' }}>
                      Soul update: values.md
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect()
                        setMenuAt({ x: rect.left, y: rect.bottom + 4 })
                      }}
                      data-testid={testIds.maaterUiGallery.entityMenuOverflow}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--ink-soft)' }}>
                    Last action: {lastOpen}
                  </p>
                  {menuAt && (
                    <EntityContextMenu
                      x={menuAt.x}
                      y={menuAt.y}
                      entries={menuEntries}
                      onClose={() => setMenuAt(null)}
                      data-testid={testIds.maaterUiGallery.entityMenuPopup}
                    />
                  )}
                </div>
              )}

              {scene === 'menu-chrome' && (
                <div className="space-y-6" data-testid={testIds.maaterUiGallery.menuChrome}>
                  <p className="text-xs text-muted-foreground">
                    Visual SoT: document viewer <code className="text-[10px]">...</code> (
                    <code className="text-[10px]">w-60</code> + scroll). Compact menus use{' '}
                    <code className="text-[10px]">QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS</code>{' '}
                    (hug width) - never force <code className="text-[10px]">w-60</code> on Sign out.
                  </p>

                  <div className="flex flex-wrap items-start gap-8">
                    <div className="space-y-2">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Long menu + scroll (viewer shape)
                      </p>
                      <div
                        className="flex items-center justify-between rounded-md border border-border px-3 py-2"
                        style={{ background: 'var(--paper-raised)' }}
                      >
                        <span className="text-sm">c.md</span>
                        <DropdownMenu defaultOpen>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              data-testid={testIds.maaterUiGallery.menuChromeViewerTrigger}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                            className={cn(
                              QUIET_OVERFLOW_MENU_CONTENT_CLASS,
                              QUIET_OVERFLOW_MENU_GALLERY_SCROLL_DEMO_CLASS,
                            )}
                          >
                            <DropdownMenuItem>
                              <List className="h-4 w-4" />
                              Show outline
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem>
                              <RefreshCw className="h-4 w-4" />
                              Refresh document
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <ExternalLink className="h-4 w-4" />
                              Google Drive
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem>
                              <Undo className="h-4 w-4" />
                              Undo last edit
                              <span className="ml-auto text-xs text-muted-foreground">Cmd+Z</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Maximize2 className="h-4 w-4" />
                              Enter fullscreen
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className={QUIET_OVERFLOW_MENU_LABEL_CLASS}>
                              Location
                            </DropdownMenuLabel>
                            <DropdownMenuItem>
                              <Folder className="h-4 w-4" />
                              Show file location
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <PanelLeft className="h-4 w-4" />
                              Reveal in sidebar
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Pin className="h-4 w-4" />
                              Pin to sidebar
                            </DropdownMenuItem>
                            <MenuChromeBookmarkDemo />
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className={QUIET_OVERFLOW_MENU_LABEL_CLASS}>
                              Document
                            </DropdownMenuLabel>
                            <DropdownMenuItem>
                              <Pencil className="h-4 w-4" />
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <FolderOpen className="h-4 w-4" />
                              Move document
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Link className="h-4 w-4" />
                              Copy URL
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Link className="h-4 w-4" />
                              Copy document path
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <FileDown className="h-4 w-4" />
                              Download PDF
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem>
                              <MessageSquare className="h-4 w-4" />
                              Add to current chat
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem>
                              <Archive className="h-4 w-4" />
                              Archive document
                            </DropdownMenuItem>
                            <DropdownMenuItem className={QUIET_MENU_DESTRUCTIVE_ITEM_CLASS}>
                              <Trash2 className="h-4 w-4" />
                              Delete document
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <p className="max-w-[15rem] text-[10px] text-muted-foreground">
                        Gallery caps height at 240px so scroll is visible; production uses 70vh.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Panel overflow (flat)
                      </p>
                      <div
                        className="flex items-center justify-between rounded-md border border-border px-3 py-2"
                        style={{ background: 'var(--paper-raised)' }}
                      >
                        <span className="text-sm">Documents</span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              data-testid={testIds.maaterUiGallery.menuChromePanelTrigger}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                            className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
                          >
                            <DropdownMenuItem>
                              <List className="h-4 w-4" />
                              Detailed layout
                              <Check className="ml-auto h-3.5 w-3.5" />
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <AlignJustify className="h-4 w-4" />
                              Dense layout
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem>
                              <Archive className="h-4 w-4" />
                              Trash
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Compact (header / sign out)
                      </p>
                      <DropdownMenu defaultOpen>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-2"
                            data-testid={testIds.maaterUiGallery.menuChromeCompactTrigger}
                          >
                            Rowan
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                          className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
                        >
                          <DropdownMenuItem>
                            <LogOut className="h-4 w-4" />
                            Sign out
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <p className="max-w-[12rem] text-[10px] text-muted-foreground">
                        Uses compact class - width hugs the label.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Date popover (quiet)
                      </p>
                      <Popover defaultOpen>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs"
                            data-testid={testIds.maaterUiGallery.menuChromeDateTrigger}
                          >
                            Sep 17, 2026
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent>
                          <Calendar selected={new Date(2026, 8, 17)} />
                        </PopoverContent>
                      </Popover>
                      <p className="max-w-[15rem] text-[10px] text-muted-foreground">
                        Same shell as overflow:{' '}
                        <code className="text-[10px]">QUIET_POPOVER_CONTENT_CLASS</code>
                        . Never stock <code className="text-[10px]">border</code> +{' '}
                        <code className="text-[10px]">shadow-md</code>.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Destructive hover (dark-safe)
                      </p>
                      <div
                        className="w-60 rounded-md border border-border/60 bg-popover p-1.5 shadow-sm"
                        data-testid={testIds.maaterUiGallery.menuChromeDestructiveHover}
                      >
                        <div className="flex items-center gap-2.5 rounded-sm px-3 py-2 text-sm">
                          <Archive className="h-4 w-4" />
                          Archive document
                        </div>
                        <div
                          className={cn(
                            'flex items-center gap-2.5 px-3 py-2 text-sm',
                            QUIET_MENU_DESTRUCTIVE_ITEM_CLASS,
                            '!bg-destructive !text-destructive-foreground',
                          )}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete document
                        </div>
                      </div>
                      <p className="max-w-[15rem] text-[10px] text-muted-foreground">
                        Hover is solid destructive + foreground text. Never dark red on a dark wash.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Confirm dialog
                      </p>
                      <div
                        className="grid w-full max-w-md gap-6 rounded-lg border border-border/60 bg-background p-6 shadow-sm"
                        data-testid={testIds.maaterUiGallery.menuChromeConfirm}
                      >
                        <div className="flex flex-col gap-3 text-left">
                          <h3 className="text-lg font-semibold">Delete document?</h3>
                          <div className="text-sm text-muted-foreground">
                            <p>
                              Are you sure you want to delete{' '}
                              <strong className="text-foreground">"Session Scratch QA Canvas"</strong>?
                            </p>
                            <p className="mt-3">
                              This will remove the document from your workspace and mark it as deleted.
                              You can undo this action from the notification.
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-row justify-end gap-2">
                          <Button variant="outline">Cancel</Button>
                          <Button variant="destructive">Delete</Button>
                        </div>
                      </div>
                      <p className="max-w-[20rem] text-[10px] text-muted-foreground">
                        Same inset for title, body, and actions. Destructive button uses the token, not{' '}
                        <code className="text-[10px]">bg-red-600</code>.
                      </p>
                    </div>
                  </div>

                  <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
                    <li>
                      Multi-section / tall: <code className="text-[10px]">QUIET_OVERFLOW_MENU_CONTENT_CLASS</code>{' '}
                      (<code className="text-[10px]">w-60</code> + scroll)
                    </li>
                    <li>
                      1-3 items: <code className="text-[10px]">QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS</code>
                    </li>
                    <li>
                      Section labels: <code className="text-[10px]">QUIET_OVERFLOW_MENU_LABEL_CLASS</code>
                    </li>
                    <li>
                      Destructive: <code className="text-[10px]">QUIET_MENU_DESTRUCTIVE_ITEM_CLASS</code> - quiet at
                      rest; hover is solid destructive + foreground (readable in dark)
                    </li>
                    <li>
                      Image search cards: under 480px of the gallery, Source + Insert collapse into{' '}
                      <code className="text-[10px]">...</code> (
                      <code className="text-[10px]">QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS</code>
                      )
                    </li>
                  </ul>

                  <div className="space-y-3">
                    <p className="text-xs font-medium text-foreground">Image search density</p>
                    <div className="flex flex-wrap items-start gap-6">
                      <div className="w-[360px] rounded-md border border-border p-2">
                        <p className="mb-1 text-[10px] text-muted-foreground">Narrow pane (360px)</p>
                        <ImageSearchGallery
                          query="Tufted Puffin"
                          results={IMAGE_SEARCH_GALLERY_DEMO_HITS}
                        />
                      </div>
                      <div className="w-[640px] rounded-md border border-border p-2">
                        <p className="mb-1 text-[10px] text-muted-foreground">Wide pane (640px)</p>
                        <ImageSearchGallery
                          query="Tufted Puffin"
                          results={IMAGE_SEARCH_GALLERY_DEMO_HITS}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {scene === 'reader-chrome' && (
                <div className="space-y-4" data-testid={testIds.maaterUiGallery.readerChrome}>
                  <p className="text-xs text-muted-foreground">
                    Based on production Inbox thread reader (`InboxReader` chrome=
                    <code>embedded</code> + `InboxThreadToolbar`). Prod is ahead of the old one-line
                    stub - this scene mirrors the live soul-proposal timeline.
                  </p>

                  <div className="overflow-hidden rounded-lg border border-border bg-card">
                    <ProtoTitleRow
                      density="inline"
                      crumbs={[
                        { id: 'inbox', label: 'Inbox' },
                        { id: 'thread', label: 'Soul update: values.md' },
                      ]}
                      title="Soul update: values.md"
                      actions={
                        <div className="flex items-center gap-1.5">
                          <Button size="sm" variant="secondary" className="h-7 text-xs">
                            Discuss
                          </Button>
                          <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                        </div>
                      }
                    />
                    <div className="flex items-center gap-2 border-b border-border px-4 py-2 text-xs text-muted-foreground">
                      <Zap className="h-3.5 w-3.5 text-yellow-500" />
                      Soul proposal · FlowNotes Product
                    </div>
                    <div className="space-y-3 px-4 py-4">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded border border-border bg-muted/30 px-2 py-1 text-xs font-medium text-primary"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        values.md
                      </button>
                      <div className="rounded border border-border bg-muted/40 p-3 text-xs leading-relaxed text-foreground">
                        <p className="mb-2 font-semibold">Proposed soul update</p>
                        <p className="mb-1 text-muted-foreground">
                          Target:{' '}
                          <span className="font-medium text-primary underline-offset-2 hover:underline">
                            values.md
                          </span>
                        </p>
                        <p className="mb-2 font-mono text-[10px] text-muted-foreground">
                          flownotes-app-docs/platform/.soul/values.md
                        </p>
                        <pre className="overflow-x-auto rounded border border-border bg-background/80 p-2 font-mono text-[10px] leading-snug">
                          <span className="text-red-600">- Prefer automatic feed ranking.</span>
                          {'\n'}
                          <span className="text-green-600">
                            + Prefer human-curated attention over opaque ranking.
                          </span>
                        </pre>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 rounded border border-sky-500/30 bg-sky-500/10 px-3 py-2">
                        <span className="text-xs font-medium text-sky-950 dark:text-sky-200">
                          Soul proposal:
                        </span>
                        <span className="text-xs font-medium text-primary">values.md</span>
                        <Button size="sm" className="h-7 text-xs">
                          Approve
                        </Button>
                        <Button size="sm" variant="secondary" className="h-7 text-xs">
                          View Diff
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs">
                          Edit
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs">
                          Dismiss
                        </Button>
                      </div>
                      <SoulProposalDecisionEvent
                        decision="approved"
                        actorLabel="Rowan"
                        at={new Date().toISOString()}
                        documentId="00000000-0000-0000-0000-000000000001"
                        filename="values.md"
                        targetPath="flownotes-app-docs/platform/.soul/values.md"
                        onOpenDocument={() => {}}
                      />
                      <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600" />
                        Approve / Dismiss append a durable <code>soul_proposal_decision</code>{' '}
                        thread message (timestamp, actor, doc link) - not metadata-only.
                      </p>
                    </div>
                  </div>

                  <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                    <li>
                      One title row only - no second h1 with Reply (see continuity Reader chrome)
                    </li>
                    <li>Document path must be openable from chip + decision event</li>
                    <li>Unresolved: Approve bar under body; resolved: decision card in timeline</li>
                  </ul>
                </div>
              )}

              {scene === 'index-vs-entity-tabs' && (
                <div className="space-y-4" data-testid={testIds.maaterUiGallery.indexVsEntity}>
                  <div>
                    <div className="mb-1 text-xs font-semibold" style={{ color: 'var(--ink)' }}>
                      Index / filter (App group)
                    </div>
                    <p className="text-xs" style={{ color: 'var(--ink-soft)' }}>
                      Home, Inbox index, Search, graph - filtered by active workspaces; no workspace
                      badge on the tab.
                    </p>
                    <div className="mt-2 flex gap-2">
                      {['Home', 'Inbox', 'Search'].map((t) => (
                        <span
                          key={t}
                          className="rounded border px-2 py-1 text-xs"
                          style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-semibold" style={{ color: 'var(--ink)' }}>
                      Workspace entity tabs
                    </div>
                    <p className="text-xs" style={{ color: 'var(--ink-soft)' }}>
                      Doc, Flow, Basecamp page, Inbox thread - group under workspace when{' '}
                      <code>workspaceId</code> is set.
                    </p>
                    <div className="mt-2 flex gap-2">
                      <span
                        className="inline-flex items-center gap-1.5 rounded border px-2 py-1 text-xs"
                        style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
                      >
                        <span
                          className="inline-flex h-4 w-4 items-center justify-center rounded text-[9px] font-bold text-white"
                          style={{ background: '#6366f1' }}
                        >
                          HS
                        </span>
                        Zoom - Auto meeting notes
                      </span>
                      <span
                        className="inline-flex items-center gap-1.5 rounded border px-2 py-1 text-xs"
                        style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
                      >
                        <span
                          className="inline-flex h-4 w-4 items-center justify-center rounded text-[9px] font-bold text-white"
                          style={{ background: '#6366f1' }}
                        >
                          HS
                        </span>
                        Soul update: values.md
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {scene === 'wait-sync' && (
                <div className="space-y-3" data-testid={testIds.maaterUiGallery.waitSync}>
                  <div className="flex flex-wrap gap-2">
                    {(['idle', 'syncing', 'synced', 'conflict'] as const).map((s) => (
                      <Button
                        key={s}
                        size="sm"
                        variant={syncState === s ? 'secondary' : 'outline'}
                        onClick={() => setSyncState(s)}
                      >
                        {s}
                      </Button>
                    ))}
                  </div>
                  <div
                    className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
                    style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
                    data-testid={testIds.maaterUiGallery.syncStrip}
                  >
                    {syncState === 'syncing' && (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: 'var(--rust)' }} />
                        <span style={{ color: 'var(--ink)' }}>Syncing with Drive…</span>
                      </>
                    )}
                    {syncState === 'synced' && (
                      <span style={{ color: 'var(--ink)' }}>Synced · just now</span>
                    )}
                    {syncState === 'conflict' && (
                      <span style={{ color: 'var(--rust)' }}>
                        Conflict - choose Maater or Drive as source of truth
                      </span>
                    )}
                    {syncState === 'idle' && (
                      <span style={{ color: 'var(--ink-soft)' }}>Idle - Keep synced off</span>
                    )}
                  </div>
                  <p className="text-xs" style={{ color: 'var(--ink-soft)' }}>
                    Screenshot mid-flight must explain every region (every-frame craft).
                  </p>
                  <a
                    href="/?test=maater-ui#list-status-chrome"
                    className="text-xs underline"
                    style={{ color: 'var(--rust)' }}
                  >
                    Related: list status chrome (bottom bar + view ...)
                  </a>
                </div>
              )}

              {scene === 'list-status-chrome' && (
                <div
                  className="space-y-4"
                  data-testid={testIds.maaterUiGallery.listStatusChrome}
                >
                  <p className="text-sm" style={{ color: 'var(--ink)' }}>
                    Index / extension list surfaces follow document-viewer quiet chrome:
                    teach with <code>(i)</code>, put sync status on a bottom bar, put
                    Reload / Refresh in view overflow <code>...</code>.
                  </p>
                  <div
                    className="overflow-hidden rounded-md border"
                    style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
                  >
                    <div
                      className="flex items-center justify-between border-b px-3 py-2"
                      style={{ borderColor: 'var(--line)' }}
                    >
                      <span className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
                        Projects
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="flex h-8 w-8 items-center justify-center rounded-md"
                          style={{ color: 'var(--ink-soft)' }}
                          title="About this list"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="flex h-8 w-8 items-center justify-center rounded-md"
                          style={{ color: 'var(--ink-soft)' }}
                          title="More actions"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="px-3 py-6 text-xs" style={{ color: 'var(--ink-faint)' }}>
                      List body (no teaching banner above rows)
                    </div>
                    <div
                      className="flex h-8 items-center justify-between border-t px-3 text-xs"
                      style={{
                        borderColor: 'var(--line)',
                        background: 'var(--paper-deep)',
                        color: 'var(--ink-soft)',
                      }}
                    >
                      <span>Cache updated · just now · 5 shown</span>
                      <Info className="h-3.5 w-3.5" />
                    </div>
                  </div>
                  <ul className="list-disc space-y-1 pl-5 text-xs" style={{ color: 'var(--ink-soft)' }}>
                    <li>
                      Reject permanent banners that explain OAuth / cache scope - use{' '}
                      <code>(i)</code> tooltip
                    </li>
                    <li>
                      Reject toolbar <strong>Reload list</strong> / <strong>Refresh visible</strong>{' '}
                      buttons - put them under view <code>...</code> (quiet overflow)
                    </li>
                    <li>
                      Mid-flight sync copy belongs on the bottom status bar (same idea as
                      document status bar)
                    </li>
                  </ul>
                  <a
                    href="/?test=basecamp-projects-cache"
                    className="text-xs underline"
                    style={{ color: 'var(--rust)' }}
                  >
                    Surface demo: Basecamp Projects cache sync
                  </a>
                </div>
              )}

              {scene === 'extension-limb' && (
                <div className="space-y-3" data-testid={testIds.maaterUiGallery.extensionLimb}>
                  <p className="text-sm" style={{ color: 'var(--ink)' }}>
                    Extension limbs use this gallery for shell, gestures, and open icons.
                  </p>
                  <ul className="list-disc space-y-1 pl-5 text-xs" style={{ color: 'var(--ink-soft)' }}>
                    <li>
                      Limb-only: Settings connect, object viewer,{' '}
                      <code>entityContextMenus</code>
                    </li>
                    <li>
                      Shared: <code>PreviewPanelToggle</code>,{' '}
                      <code>ExtensionSettingsShell</code>, <code>SidebarTreeRow</code>
                    </li>
                    <li>
                      Skill: <code>maater-extension-ui</code> - do not invent row CSS or parallel menus
                    </li>
                  </ul>
                  <a
                    href="/?test=basecamp-extension"
                    className="text-xs underline"
                    style={{ color: 'var(--rust)' }}
                  >
                    Surface demo: Basecamp extension
                  </a>
                </div>
              )}

              {scene === 'shell' && (
                <div className="space-y-3" data-testid={testIds.maaterUiGallery.shellScene}>
                  <p className="text-sm" style={{ color: 'var(--ink)' }}>
                    Compose from <code>@/components/prototype/maater-proto-*</code>:
                  </p>
                  <ul className="list-disc space-y-1 pl-5 text-xs" style={{ color: 'var(--ink-soft)' }}>
                    <li>
                      Gallery skin: <code>maaterProtoAppSkinStyle()</code> + ThemeProvider light/dark
                      (one picker in header)
                    </li>
                    <li>ProtoAppHeader / ProtoIconRail / ProtoTabRow / ProtoTitleRow</li>
                    <li>ProtoModePanel for nouns; main list for verbs</li>
                    <li>Secondary preview below title row, beside content</li>
                    <li>
                      Nested nouns: <code>ProtoStackNav</code> (
                      <button
                        type="button"
                        className="underline"
                        style={{ color: 'var(--rust)' }}
                        onClick={() => selectScene('mode-panel-stack')}
                      >
                        #mode-panel-stack
                      </button>
                      )
                    </li>
                    <li>
                      Frame fidelity:{' '}
                      <button
                        type="button"
                        className="underline"
                        style={{ color: 'var(--rust)' }}
                        onClick={() => selectScene('harness')}
                      >
                        #harness
                      </button>{' '}
                      (TOC + Info dock + selected rows)
                    </li>
                  </ul>
                  <p className="text-xs" style={{ color: 'var(--ink-soft)' }}>
                    Paper / Ink / Stone stay on landing, mobile, and feature <code>?test=</code>{' '}
                    protos - not this gallery.
                  </p>
                </div>
              )}

              {scene === 'harness' && (
                <div className="space-y-4" data-testid={testIds.maaterUiGallery.harnessScene}>
                  <p className="text-sm text-muted-foreground">
                    Live webapp frame contracts. Captures:{' '}
                    <code className="text-xs">docs/design/captures/2026-08-02-harness/</code>. Icon
                    rail is not TOC - TOC sits left of the document canvas; Info dock on the right.
                  </p>
                  <div
                    className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground"
                    data-testid={testIds.maaterUiGallery.harnessSecondarySlot}
                  >
                    <p className="font-medium text-foreground">One secondary slot (preview XOR info)</p>
                    <p className="mt-1">
                      Index surfaces (Home, workspace home, Inbox, Search, Browse folder) use{' '}
                      <code>PanelRight</code> Continuity preview only. Entity surfaces (document,
                      Drive file, Basecamp todo) use the Info dock <code>(i)</code> only. Never
                      stack both. Classifier:{' '}
                      <code>resolveShellInfoDockKind</code> in{' '}
                      <code>document-viewer.utils</code> - indexes return <code>null</code>.
                    </p>
                  </div>

                  <div className="grid gap-3 lg:grid-cols-3">
                    <div className="rounded-md border border-border bg-card p-3">
                      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Mode panel selected
                      </div>
                      <p className="mb-2 text-sm text-muted-foreground">
                        Same pattern as Inbox Unread / Browse Open Tabs - soft fill + left bar.
                        Gallery Scenes list uses ModePanelNavItem (text-sm).
                      </p>
                      <div className="ws-nav-section -mx-1 flex flex-col" style={wsNavSectionStyle()}>
                        {(
                          [
                            { id: 'unread' as const, label: 'Unread', badge: '1' },
                            { id: 'inbox' as const, label: 'Inbox', badge: '1' },
                            { id: 'flows' as const, label: 'Flows' },
                          ] as const
                        ).map((row) => (
                          <ModePanelNavItem
                            key={row.id}
                            active={harnessNav === row.id}
                            label={row.label}
                            onClick={() => setHarnessNav(row.id)}
                            trailing={
                              'badge' in row && row.badge ? (
                                <span className="text-sm tabular-nums text-muted-foreground">
                                  {row.badge}
                                </span>
                              ) : undefined
                            }
                          />
                        ))}
                      </div>
                    </div>

                    <div
                      className="flex h-[280px] flex-col overflow-hidden rounded-md border border-border"
                      data-testid={testIds.maaterUiGallery.harnessToc}
                    >
                      <div className="flex items-center justify-between border-b border-border px-3 py-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Contents
                        </span>
                        <span className="text-[10px] text-muted-foreground">TOC rail</span>
                      </div>
                      <nav className="flex-1 space-y-0.5 overflow-y-auto p-2 text-xs">
                        <button type="button" className="block w-full truncate px-2 py-1 text-left font-medium text-primary">
                          Self-Analysis: Basecamp…
                        </button>
                        <button type="button" className="block w-full truncate px-2 py-1 pl-4 text-left text-muted-foreground hover:text-foreground">
                          Overview
                        </button>
                        <button type="button" className="block w-full truncate px-2 py-1 pl-4 text-left text-muted-foreground hover:text-foreground">
                          The Five Todos
                        </button>
                        <button type="button" className="block w-full truncate px-2 py-1 pl-4 text-left text-muted-foreground hover:text-foreground">
                          Strengths
                        </button>
                      </nav>
                      <p className="border-t border-border px-3 py-2 text-[10px] text-muted-foreground">
                        Import production <code>DocumentToc</code> in real docs. Extensions: do not
                        put heading outline in the mode panel.
                      </p>
                    </div>

                    <div
                      className="h-[280px] overflow-hidden rounded-md border border-border"
                      data-testid={testIds.maaterUiGallery.harnessInfoDock}
                    >
                      <DocumentInfoDock
                        mode={infoDockMode}
                        onModeChange={setInfoDockMode}
                        onClose={() => {}}
                      >
                        <div className="space-y-3 p-4 text-xs">
                          {infoDockMode === 'details' ? (
                            <>
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                  Type
                                </div>
                                <div className="mt-0.5 text-foreground">Note</div>
                              </div>
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                  Status
                                </div>
                                <span className="mt-1 inline-flex rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-primary">
                                  active
                                </span>
                              </div>
                              <p className="text-muted-foreground">
                                Extensions: reuse <code>DocumentInfoDock</code> chrome; swap Details
                                body; omit History with <code>showHistoryTab=false</code> when N/A.
                              </p>
                            </>
                          ) : (
                            <p className="text-muted-foreground">
                              History mode - version list / edit timeline lives here in production.
                            </p>
                          )}
                        </div>
                      </DocumentInfoDock>
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                    <strong className="text-foreground">Keep from prototype:</strong> muted tab-bar
                    strip (already on ProtoTabRow).{' '}
                    <strong className="text-foreground">Match production:</strong> selected row left
                    bar, Details/History segment, TOC left / Info right of canvas. See inventory
                    Harness gap backlog.
                  </div>
                </div>
              )}

              {scene === 'mode-panel-stack' && (
                <div
                  className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row"
                  data-testid={testIds.maaterUiGallery.modePanelStack}
                >
                  <div
                    className="flex h-[420px] w-full max-w-[260px] shrink-0 flex-col overflow-hidden rounded-lg border"
                    style={{
                      borderColor: 'var(--line)',
                      background: 'var(--paper-raised)',
                    }}
                  >
                    <div
                      className="shrink-0 border-b px-3 py-2 text-xs font-medium"
                      style={{ borderColor: 'var(--line)', color: 'var(--ink-soft)' }}
                    >
                      Live stack nav
                    </div>
                    <ProtoStackNav
                      items={STACK_NAV_DEMO}
                      activeId={stackLeaf}
                      testId={testIds.maaterUiGallery.stackNav}
                      onSelectLeaf={(id, trail) => {
                        setStackLeaf(id)
                        setStackTrail(trail.map((t) => t.label).join(' / '))
                      }}
                    />
                  </div>
                  <div className="min-w-0 flex-1 space-y-3">
                    <p className="text-sm" style={{ color: 'var(--ink)' }}>
                      Nested mode depth without expand/collapse trees. Click a row with a
                      chevron: the list is replaced; header becomes back + centered title
                      (Vercel Observability pattern).
                    </p>
                    <p className="text-xs" style={{ color: 'var(--ink-soft)' }}>
                      Selected leaf: <span style={{ color: 'var(--ink)' }}>{stackLeaf}</span>
                      {' · '}
                      Trail: <span style={{ color: 'var(--ink)' }}>{stackTrail}</span>
                    </p>
                    <div
                      className="overflow-x-auto rounded-md border text-xs"
                      style={{ borderColor: 'var(--line)' }}
                    >
                      <table className="w-full text-left">
                        <thead style={{ background: 'var(--paper-deep)', color: 'var(--ink-soft)' }}>
                          <tr>
                            <th className="px-3 py-2 font-medium">Pattern</th>
                            <th className="px-3 py-2 font-medium">When</th>
                            <th className="px-3 py-2 font-medium">Maater today</th>
                          </tr>
                        </thead>
                        <tbody style={{ color: 'var(--ink)' }}>
                          <tr className="border-t" style={{ borderColor: 'var(--line)' }}>
                            <td className="px-3 py-2 font-medium">Stack drill (this)</td>
                            <td className="px-3 py-2" style={{ color: 'var(--ink-soft)' }}>
                              Deep noun IA; one level visible at a time
                            </td>
                            <td className="px-3 py-2" style={{ color: 'var(--ink-soft)' }}>
                              ProtoStackNav - candidate for Settings / Studio nav
                            </td>
                          </tr>
                          <tr className="border-t" style={{ borderColor: 'var(--line)' }}>
                            <td className="px-3 py-2 font-medium">Expand in place</td>
                            <td className="px-3 py-2" style={{ color: 'var(--ink-soft)' }}>
                              Shallow desk (My documents, Bookmarks)
                            </td>
                            <td className="px-3 py-2" style={{ color: 'var(--ink-soft)' }}>
                              ADR-0019 Browse desk / Basecamp Bookmarks
                            </td>
                          </tr>
                          <tr className="border-t" style={{ borderColor: 'var(--line)' }}>
                            <td className="px-3 py-2 font-medium">Folder focus + crumbs</td>
                            <td className="px-3 py-2" style={{ color: 'var(--ink-soft)' }}>
                              File tree zoom; path still visible
                            </td>
                            <td className="px-3 py-2" style={{ color: 'var(--ink-soft)' }}>
                              Documents explorer folder-focus
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <ul className="list-disc space-y-1 pl-5 text-xs" style={{ color: 'var(--ink-soft)' }}>
                      <li>Row height ~36px; 16px icons in 36px grid; 13px medium labels</li>
                      <li>Hover: soft raised wash; active: paperDeep (no purple pills)</li>
                      <li>Parents show trailing chevron; leaves select into main</li>
                      <li>
                        Import: <code>ProtoStackNav</code> from{' '}
                        <code>maater-proto-chrome</code>
                      </li>
                      <li>
                        Do not replace ADR-0019 expand desk with stack without a product call
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {scene === 'panel-collapse' && <PanelCollapseDemo />}
              {scene === 'panel-motion' && <PanelMotionScene />}
              {scene === 'tab-groups' && <TabGroupsScene />}
              {scene === 'open-echo' && <OpenEchoScene />}
              {scene === 'presence' && <PresenceScene />}
              {scene === 'settings-chrome' && <SettingsChromeScene />}
              {scene === 'list-overflow' && <ListOverflowScene />}
              {scene === 'create-in-folder' && <CreateInFolderScene />}
              {scene === 'person-view' && <PersonViewScene />}
              {scene === 'toasts' && <ToastChromeScene />}
              {scene === 'composer-offers' && <ComposerOffersScene />}
              {scene === 'diff-review' && <DiffReviewScene />}
              {scene === 'inbox-attention' && <InboxAttentionScene />}
            </div>

            {(scene === 'list-gestures' || scene === 'entity-menu') && previewOpen && (
              <aside
                className="w-[280px] shrink-0 overflow-y-auto border-l p-3"
                style={{ borderColor: 'var(--line)', background: 'var(--paper-deep)' }}
                data-testid={testIds.maaterUiGallery.previewPane}
              >
                {selectedRow ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: 'var(--ink)' }}>
                      <OpenDestinationIcons.preview className="h-3.5 w-3.5" />
                      Preview
                    </div>
                    <p className="text-sm" style={{ color: 'var(--ink)' }}>
                      Selected: {selectedRow}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1 text-xs"
                      onClick={() => setLastOpen(`Open ${selectedRow}`)}
                    >
                      <OpenIcon className="h-3.5 w-3.5" />
                      Open
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs" style={{ color: 'var(--ink-soft)' }}>
                    Select a document, or choose Preview from the row menu.
                  </p>
                )}
              </aside>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}
